import { useEffect, useState } from "react";
import api from "@/lib/api";
import PageShell from "@/components/PageShell";
import { Loader2, Clock, Target, X } from "lucide-react";

const diffColor = { Beginner: "bg-[#E6F4EA] text-[#2E7D32]", Intermediate: "bg-[#FEF3E0] text-[#C67D0A]", Advanced: "bg-[#FAF0F1] text-[#7A222B]" };

export default function Projects() {
  const [items, setItems] = useState(null);
  const [cat, setCat] = useState("All");
  const [active, setActive] = useState(null);

  useEffect(() => { api.get("/projects").then((r) => setItems(r.data)).catch(() => setItems([])); }, []);

  const cats = ["All", ...Array.from(new Set((items || []).map((i) => i.category)))];
  const filtered = (items || []).filter((i) => cat === "All" || i.category === cat);

  return (
    <PageShell>
      <div>
        <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#7A222B]">AI Projects</span>
        <h1 className="font-display mt-2 text-3xl sm:text-4xl font-bold tracking-tight text-[#2D2825]" style={{ fontWeight: 700 }}>Build proof of work</h1>
        <p className="mt-3 text-[#6E665E] max-w-2xl">Complete real-world AI project briefs to prove your skills — no experience required to start.</p>
      </div>

      <div className="mt-8 flex gap-2 flex-wrap">
        {cats.map((c) => (
          <button key={c} onClick={() => setCat(c)} data-testid={`project-filter-${c.toLowerCase().replace(/\s/g, "-")}`}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${cat === c ? "bg-[#7A222B] text-white" : "bg-white border border-[#EAE3DB] text-[#6E665E] hover:border-[#D99B9F]"}`}>
            {c}
          </button>
        ))}
      </div>

      {items === null ? (
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#7A222B]" /></div>
      ) : (
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((p) => (
            <button key={p.id} onClick={() => setActive(p)} data-testid={`project-card-${p.id}`} className="text-left bg-white border border-[#EAE3DB] rounded-3xl p-7 hover:shadow-xl hover:shadow-[#2D2825]/5 transition-shadow">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold px-3 py-1 rounded-full bg-[#F5EFE6] text-[#7A222B]">{p.category}</span>
                <span className={`text-xs font-semibold px-3 py-1 rounded-full ${diffColor[p.difficulty] || ""}`}>{p.difficulty}</span>
              </div>
              <h3 className="mt-5 text-lg font-semibold text-[#2D2825]">{p.title}</h3>
              <p className="mt-2 text-sm text-[#6E665E] leading-relaxed line-clamp-3">{p.brief}</p>
              <div className="mt-5 pt-4 border-t border-[#EAE3DB] flex items-center gap-2 text-xs text-[#948A80]">
                <Clock className="w-3.5 h-3.5" /> {p.duration}
              </div>
            </button>
          ))}
        </div>
      )}

      {active && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#2D2825]/50 backdrop-blur-sm" onClick={() => setActive(null)}>
          <div className="bg-[#FDFBF7] rounded-3xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-8" onClick={(e) => e.stopPropagation()} data-testid="project-modal">
            <div className="flex items-start justify-between">
              <div className="flex gap-2">
                <span className="text-xs font-semibold px-3 py-1 rounded-full bg-[#F5EFE6] text-[#7A222B]">{active.category}</span>
                <span className={`text-xs font-semibold px-3 py-1 rounded-full ${diffColor[active.difficulty] || ""}`}>{active.difficulty}</span>
              </div>
              <button onClick={() => setActive(null)} data-testid="project-modal-close" className="text-[#948A80] hover:text-[#2D2825] transition-colors"><X className="w-5 h-5" /></button>
            </div>
            <h3 className="mt-4 font-display text-2xl font-bold text-[#2D2825]" style={{ fontWeight: 700 }}>{active.title}</h3>
            <p className="mt-3 text-sm text-[#6E665E] leading-relaxed">{active.brief}</p>
            <div className="mt-6">
              <h4 className="flex items-center gap-2 font-semibold text-[#2D2825]"><Target className="w-4 h-4 text-[#7A222B]" /> Deliverables</h4>
              <ul className="mt-3 space-y-2">
                {active.deliverables?.map((d) => <li key={d} className="text-sm text-[#6E665E] flex gap-2"><span className="text-[#7A222B]">•</span> {d}</li>)}
              </ul>
            </div>
            <div className="mt-6 flex flex-wrap gap-1.5">
              {active.skills?.map((s) => <span key={s} className="text-xs bg-[#FDF0EC] text-[#7A222B] px-2.5 py-1 rounded-full">{s}</span>)}
            </div>
            <p className="mt-6 text-xs text-[#948A80] flex items-center gap-2"><Clock className="w-3.5 h-3.5" /> Suggested time: {active.duration}</p>
          </div>
        </div>
      )}
    </PageShell>
  );
}
